import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import DeleteConfirmOverlay from '../components/DeleteConfirmOverlay';
import { InlineLoader } from '../components/loading';
import { RetryErrorState } from '../components/RetryErrorState';
import { CenteredLoader } from '../components/CenteredLoader';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, Loader2, Shield, UserPlus, Users } from 'lucide-react';
import { rolesAPI, employeesAPI, projectConfigAPI, projectsAPI, externalUsersAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { externalUserCredentialsToSend, type ExternalUserCredentialsToSend } from '../utils/externalUserCredentials';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
}

interface UserRoleAssignment {
  id: string;
  user_name: string;
  role_id: string;
  role_name: string;
  role_description: string | null;
  assigned_at: string;
}

interface Employee {
  id: string;
  emp_code: string;
  empCode: string;
  employeeCode: string;
  emp_name: string;
  empName: string;
  employeeName: string;
  designation: string;
  budgetName?: string;
  budgetId?: number;
  isActiveEmployee?: boolean;
}

export default function UserRoleAssignmentPage() {
  const { selectedProjectId, user } = useAuthStore();
  const isSuperAdmin = user?.adminRole === 'super_admin';
  const isAnyAdmin = isSuperAdmin || user?.adminRole === 'admin';

  const [assignments, setAssignments] = useState<UserRoleAssignment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A link can open this page on one person: ?q=<their employee code>, the free-text
  // value of the drill-down links (utils/drilldown). The Data Hygiene dashboard's rows are
  // such links. The search box starts with it, once, as the page opens.
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => (searchParams.get('q') ?? '').trim());
  // Then q leaves the address (replaced, so Back does not bring it back): the box is the
  // person's from then on, and the address would go on naming a search they have since
  // changed.
  useEffect(() => {
    if (!searchParams.has('q')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [roleFilter, setRoleFilter] = useState<string>('');

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ userId: string; roleId: string } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<UserRoleAssignment | null>(null);
  const [editRoleId, setEditRoleId] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editMobileOriginal, setEditMobileOriginal] = useState('');
  const [editExtName, setEditExtName] = useState('');
  const [editExtNameOriginal, setEditExtNameOriginal] = useState('');
  // Web access fields in the Edit modal for external users. Password stays
  // blank unless the admin is rotating it.
  const [editExtEmail, setEditExtEmail] = useState('');
  const [editExtEmailOriginal, setEditExtEmailOriginal] = useState('');
  const [editExtPassword, setEditExtPassword] = useState('');
  const [editExtHasWebAccessOriginal, setEditExtHasWebAccessOriginal] = useState(false);
  const [editExtShowPassword, setEditExtShowPassword] = useState(false);
  // A project Admin's Edit: what the server says of this person's name and mobile.
  const [editExtDetails, setEditExtDetails] = useState<'waiting' | 'open' | 'locked' | 'not_listed' | 'failed'>('waiting');

  // Form state
  const [assignForm, setAssignForm] = useState({
    user_name: '',
    role_id: '',
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedBudget, setSelectedBudget] = useState<string>('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Super admin: toggle between project employees, search all employees, and add external user
  const [assignMode, setAssignMode] = useState<'project' | 'search' | 'external'>('project');
  const [externalSearch, setExternalSearch] = useState('');
  const [externalResults, setExternalResults] = useState<{ employee_code: string; employee_name: string; email: string }[]>([]);
  const [externalSearchLoading, setExternalSearchLoading] = useState(false);
  const [selectedExternalEmployees, setSelectedExternalEmployees] = useState<string[]>([]);
  const [externalRoleId, setExternalRoleId] = useState('');

  // External user form (add user not in 1Office)
  const [extUserName, setExtUserName] = useState('');
  const [extUserMobile, setExtUserMobile] = useState('');
  const [extUserRoleId, setExtUserRoleId] = useState('');
  // Optional web access credentials. Leave blank to keep the user mobile-only.
  const [extUserEmail, setExtUserEmail] = useState('');
  const [extUserPassword, setExtUserPassword] = useState('');
  const [extUserShowPassword, setExtUserShowPassword] = useState(false);

  // Identity-reuse detection — when the admin types email or mobile of an
  // existing external user, we surface a banner and switch the form into
  // "linking to existing user" mode.
  type ExistingExtUser = {
    employeeCode: string;
    name: string;
    mobile: string;
    email: string | null;
    hasPassword: boolean;
    isActive: boolean;
    currentProjects: { projectId: number; projectName: string | null; roleName: string | null }[];
  };
  const [extExistingUser, setExtExistingUser] = useState<ExistingExtUser | null>(null);
  const [extLookupConflict, setExtLookupConflict] = useState<string | null>(null);
  const [extChangePassword, setExtChangePassword] = useState(false);
  // Who may use Add External User is the server's answer, not adminRole: login calls every
  // role holder an admin. 'platform' = on the admin list; 'project' = holds an Admin role in
  // this project, who may add people and correct the name and mobile of those the server allows.
  const [externalAccess, setExternalAccess] = useState<{ projectId: number; level: 'platform' | 'project' | null } | null>(null);
  const externalLevel = isSuperAdmin
    ? 'platform'
    : externalAccess?.projectId === selectedProjectId ? externalAccess?.level ?? null : null;
  const accessKnown = isSuperAdmin || externalAccess?.projectId === selectedProjectId;
  const canEditExternalDetails = externalLevel === 'platform';
  const addsByNameAndMobile = externalLevel === 'project';
  const lockedToExisting = addsByNameAndMobile && !!extExistingUser;
  // What the name box held before a match filled it in, to put back when the match goes.
  const nameBeforeMatch = useRef<string | null>(null);
  const clearMatch = () => {
    setExtExistingUser(null);
    setExtLookupConflict(null);
    if (nameBeforeMatch.current !== null) {
      setExtUserName(nameBeforeMatch.current);
      nameBeforeMatch.current = null;
    }
  };
  // Asked again as a window opens, so a failed first ask does not hide anything for good.
  // A failure keeps the last answer: the save is checked by the server either way.
  useEffect(() => {
    if (!selectedProjectId || isSuperAdmin) return;
    let current = true;
    externalUsersAPI.access(selectedProjectId)
      .then((res) => {
        if (current) setExternalAccess({ projectId: selectedProjectId, level: res.data?.level ?? null });
      })
      .catch((err) => console.warn('External user access check failed:', err));
    return () => { current = false; };
  }, [selectedProjectId, isSuperAdmin, showAssignModal, showEditModal]);
  const [pendingExtPropagationConfirm, setPendingExtPropagationConfirm] = useState<null | (() => void)>(null);

  // Budget access from Project Management keyed by budget_id (web/mobile/both/none)
  const [budgetAccessMap, setBudgetAccessMap] = useState<Record<number, string>>({});
  // Budgets from legacy API
  const [legacyBudgets, setLegacyBudgets] = useState<{ budgetId: number; budgetName: string }[]>([]);

  // Debounced lookup — fires when admin types email or mobile in the Add
  // External User form. Detects identity reuse so we can prefill name and
  // signal that an existing password will be used.
  useEffect(() => {
    if (assignMode !== 'external') {
      clearMatch();
      return;
    }
    // Their match was for one number. Another number is another person, even before the
    // look-up answers: a form locked to the wrong person must not be savable.
    if (addsByNameAndMobile && extExistingUser && extExistingUser.mobile !== extUserMobile.trim()) clearMatch();
    const trimmedEmail = extUserEmail.trim();
    const cleanMobile = extUserMobile.trim();
    const emailComplete = trimmedEmail.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const mobileComplete = /^[6-9]\d{9}$/.test(cleanMobile);

    if (!emailComplete && !mobileComplete) {
      clearMatch();
      return;
    }

    let current = true;
    const ctrl = setTimeout(async () => {
      try {
        const params: { email?: string; mobile?: string; projectId?: number } = {};
        if (selectedProjectId) params.projectId = selectedProjectId;
        if (emailComplete) params.email = trimmedEmail;
        if (mobileComplete) params.mobile = cleanMobile;
        const res = await externalUsersAPI.lookup(params);
        // The form has moved on: this answer is about what it held before.
        if (!current) return;
        const data = res.data || {};
        if (data.conflict) {
          clearMatch();
          setExtLookupConflict(data.message || 'Email and mobile belong to different users.');
          return;
        }
        if (data.found && data.user) {
          setExtLookupConflict(null);
          setExtExistingUser(data.user as ExistingExtUser);
          // Prefill name on first detection (but don't clobber edits made
          // after the user was detected).
          if (addsByNameAndMobile) {
            if (nameBeforeMatch.current === null) nameBeforeMatch.current = extUserName;
            setExtUserName(data.user.name || '');
          } else if (!extUserName.trim()) {
            setExtUserName(data.user.name || '');
          }
          if (!emailComplete && data.user.email) setExtUserEmail(data.user.email);
          if (!mobileComplete && data.user.mobile) setExtUserMobile(data.user.mobile);
          // If admin hasn't expanded the password override and the existing
          // identity has a password, clear stale typing in the password field.
          if (!extChangePassword && data.user.hasPassword) setExtUserPassword('');
        } else {
          clearMatch();
        }
      } catch (err) {
        // Silent — lookup is best-effort. Submit will surface any real error.
        console.warn('External user lookup failed:', err);
      }
    }, 350);
    return () => {
      current = false;
      clearTimeout(ctrl);
    };
    // We intentionally exclude extUserName / extChangePassword from deps to
    // avoid re-querying on every keystroke into those fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignMode, extUserEmail, extUserMobile]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      const [assignmentsRes, rolesRes, employeesRes] = await Promise.all([
        rolesAPI.getAllAssignments(selectedProjectId),
        rolesAPI.getByProject(selectedProjectId),
        employeesAPI.getProjectEmployees(true, selectedProjectId, true),
      ]);

      if (assignmentsRes.data.success) {
        setAssignments(assignmentsRes.data.data);
      }
      if (rolesRes.data.success) {
        setRoles(rolesRes.data.data);
      }
      // Handle both response formats: { success: true } and { status: 'success' }
      // project-employees returns { data: { deployed, undeployed, all } }
      if (employeesRes.data.success || employeesRes.data.status === 'success') {
        const empData = employeesRes.data.data;
        // Use 'all' array if available (from project-employees), otherwise use data directly
        setEmployees(empData?.all || empData || []);
      }

      // Fetch budgets from legacy API + budget application access
      if (selectedProjectId) {
        try {
          const [budgetsRes, accessRes] = await Promise.all([
            projectsAPI.getActiveBudgets(selectedProjectId),
            projectConfigAPI.getBudgetAccess(selectedProjectId),
          ]);
          setLegacyBudgets(budgetsRes.data?.data || []);
          const rows: { budget_id: number; budget_name: string; application_access: string }[] = accessRes.data?.data || [];
          const map: Record<number, string> = {};
          rows.forEach((row) => {
            if (row.budget_id) map[row.budget_id] = row.application_access;
          });
          setBudgetAccessMap(map);
        } catch (err) {
          console.error('Failed to fetch budgets/access:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) fetchData();
  }, [fetchData, selectedProjectId]);

  // Filter assignments — only show active employees
  const filteredAssignments = assignments.filter(
    (assignment) => {
      const isActive = (assignment as any).is_active_employee;
      if (isActive === false) return false;

      if (roleFilter && assignment.role_id !== roleFilter) return false;

      const q = searchQuery.toLowerCase();
      return (
        assignment.user_name.toLowerCase().includes(q) ||
        assignment.role_name.toLowerCase().includes(q) ||
        ((assignment as any).employee_name || '').toLowerCase().includes(q)
      );
    }
  );

  // Same filter minus the search query — drives the headline count so it
  // stays comparable with the "X users" number under each role on Access
  // Management (which doesn't know about the search query either).
  const roleScopedActiveAssignments = assignments.filter((assignment) => {
    const isActive = (assignment as any).is_active_employee;
    if (isActive === false) return false;
    if (roleFilter && assignment.role_id !== roleFilter) return false;
    return true;
  });
  const selectedRoleName = roleFilter
    ? roles.find((r) => r.id === roleFilter)?.name
    : undefined;



  // Get budgets from legacy API, filtered to only those with web or both access
  const distinctBudgets = legacyBudgets
    .filter((b) => {
      const access = budgetAccessMap[b.budgetId];
      return access === 'web' || access === 'both';
    })
    .map((b) => ({ name: b.budgetName, id: b.budgetId }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Codes of employees who already have a role assigned in this project. Used
  // to hide them from the assignment dropdowns — re-assigning is done via the
  // Edit action on the existing row, not through "Add & Assign Role" again.
  const assignedUserCodes = new Set(assignments.map((a) => a.user_name));

  // Filter employees — only active, then by selected budget, then by search,
  // then drop anyone who already has a role in this project.
  const filteredEmployees = employees
    .filter((emp) => emp.isActiveEmployee !== false)
    .filter((emp) => !selectedBudget || (emp.budgetId?.toString() === selectedBudget))
    .filter((emp) => {
      const code = emp.emp_code || emp.empCode || emp.employeeCode || emp.id;
      return !assignedUserCodes.has(code);
    })
    .filter((emp) => {
      const code = emp.emp_code || emp.empCode || emp.employeeCode || emp.id;
      const name = emp.emp_name || emp.empName || emp.employeeName || '';
      const search = employeeSearch.toLowerCase();
      return (
        code?.toString().toLowerCase().includes(search) ||
        name.toLowerCase().includes(search)
      );
    });

  // Same exclusion for the "Search All Employees" results — drop anyone whose
  // employee_code is already assigned in this project.
  const filteredExternalResults = externalResults.filter(
    (emp) => !assignedUserCodes.has(emp.employee_code)
  );

  // Get employee name by ID — checks employee list first, then backend-resolved name
  const getEmployeeName = (userId: string) => {
    const emp = employees.find(
      (e) => (e.emp_code || e.empCode || e.employeeCode || e.id) === userId
    );
    if (emp) {
      const name = emp.emp_name || emp.empName || emp.employeeName;
      if (name && name !== userId) return name;
    }
    // Fallback: check employee_name from backend-resolved assignment data
    const assignment = assignments.find((a) => a.user_name === userId);
    if ((assignment as any)?.employee_name) return (assignment as any).employee_name;
    return userId;
  };

  // Get employee mobile — prefer backend-resolved mobile from assignment, fallback to employees list
  const getEmployeeMobile = (assignment: UserRoleAssignment): string => {
    const fromBackend = (assignment as any).employee_mobile;
    if (fromBackend) return fromBackend;
    const emp = employees.find(
      (e) => (e.emp_code || e.empCode || e.employeeCode || e.id) === assignment.user_name
    );
    return ((emp as any)?.mobile || (emp as any)?.contact_number || (emp as any)?.contactNo || '').toString();
  };

  // Get employee active status by ID — checks project employee list first, then master DB status from backend
  const getEmployeeActiveStatus = (userId: string): boolean | undefined => {
    const emp = employees.find(
      (e) => (e.emp_code || e.empCode || e.employeeCode || e.id) === userId
    );
    if (emp?.isActiveEmployee !== undefined) return emp.isActiveEmployee;
    // Fallback: use is_active_employee from backend-resolved assignment data
    const assignment = assignments.find((a) => a.user_name === userId);
    const backendStatus = (assignment as any)?.is_active_employee;
    if (backendStatus !== null && backendStatus !== undefined) return backendStatus;
    return undefined;
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
      setSelectAll(false);
    } else {
      const allCodes = filteredEmployees.map(
        (emp) => emp.emp_code || emp.empCode || emp.employeeCode || emp.id
      );
      setSelectedEmployees(allCodes);
      setSelectAll(true);
    }
  };

  // Handle individual employee checkbox
  const handleEmployeeToggle = (code: string) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(code)) {
        const newSelection = prev.filter((c) => c !== code);
        setSelectAll(false);
        return newSelection;
      } else {
        const newSelection = [...prev, code];
        if (newSelection.length === filteredEmployees.length) {
          setSelectAll(true);
        }
        return newSelection;
      }
    });
  };

  // Handle assign
  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedEmployees.length === 0 || !assignForm.role_id) {
      alert('Please select at least one employee and a role');
      return;
    }

    setActionLoading(true);
    try {
      // Assign role to all selected employees
      await Promise.all(
        selectedEmployees.map((userId) =>
          rolesAPI.assignRole(userId, assignForm.role_id)
        )
      );
      fetchData();
      setShowAssignModal(false);
      setAssignForm({ user_name: '', role_id: '' });
      setEmployeeSearch('');
      setSelectedBudget('');
      setSelectedEmployees([]);
      setSelectAll(false);
    } catch (err: any) {
      console.error('Error assigning role:', err);
      alert(err.response?.data?.message || 'Failed to assign role');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle edit assignment
  const handleEditAssignment = async (assignment: UserRoleAssignment) => {
    setEditingAssignment(assignment);
    setEditRoleId(assignment.role_id);
    setEditMobile('');
    setEditMobileOriginal('');
    setEditExtName('');
    setEditExtNameOriginal('');
    setEditExtEmail('');
    setEditExtEmailOriginal('');
    setEditExtPassword('');
    setEditExtHasWebAccessOriginal(false);
    setEditExtShowPassword(false);
    setEditExtDetails('waiting');
    setShowEditModal(true);
  };

  // An external user's current name and mobile number, once this person is known to be
  // someone who may change them: the access answer can land after the window opens.
  const editingExternalCode = showEditModal && editingAssignment?.user_name.startsWith('EXT-') ? editingAssignment.user_name : null;
  const mayEditExternalDetails = canEditExternalDetails || editExtDetails === 'open';
  useEffect(() => {
    if (!editingExternalCode || externalLevel === null || !selectedProjectId) return;
    let current = true;
    externalUsersAPI.getByProject(selectedProjectId)
      .then((res) => {
        if (!current) return;
        const extUser = (res.data?.data || []).find((u: any) => u.employee_code === editingExternalCode);
        const mayEdit = externalLevel === 'platform' || extUser?.can_edit_details === true;
        setEditExtDetails(!extUser ? 'not_listed' : mayEdit ? 'open' : 'locked');
        if (extUser && mayEdit) {
          setEditMobile(extUser.mobile || '');
          setEditMobileOriginal(extUser.mobile || '');
          setEditExtName(extUser.name || '');
          setEditExtNameOriginal(extUser.name || '');
          setEditExtEmail(extUser.email || '');
          setEditExtEmailOriginal(extUser.email || '');
          setEditExtHasWebAccessOriginal(!!extUser.has_web_access);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch external user details:', err);
        if (current) setEditExtDetails('failed');
      });
    return () => { current = false; };
  }, [editingExternalCode, externalLevel, selectedProjectId]);

  // Handle update role
  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAssignment || !editRoleId) return;

    // Validate fields for external users
    const isExternal = editingAssignment.user_name.startsWith('EXT-') && mayEditExternalDetails;
    const mobileChanged = isExternal && editMobile.trim() !== editMobileOriginal;
    const nameChanged = isExternal && editExtName.trim() !== editExtNameOriginal;
    const trimmedEmail = editExtEmail.trim();
    const emailChanged = isExternal && trimmedEmail !== (editExtEmailOriginal || '');
    const passwordChanged = isExternal && editExtPassword.length > 0;

    if (isExternal && !editExtName.trim()) {
      alert('Name cannot be empty');
      return;
    }
    if (mobileChanged) {
      const cleanMobile = editMobile.trim().replace(/\s+/g, '');
      if (!/^[6-9]\d{9}$/.test(cleanMobile)) {
        alert('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    // Web-access rules on edit:
    //   - If the user had web access before and email is now cleared, also
    //     clear the password — we'll send explicit nulls to revoke access.
    //   - If email is set and the user had no password before, require a new
    //     password to be typed (can't grant web access without one).
    //   - If email is unchanged and only password was typed, we can rotate it.
    let emailPayload: string | null | undefined = undefined;
    let passwordPayload: string | null | undefined = undefined;
    if (isExternal) {
      const hadWebAccess = editExtHasWebAccessOriginal;
      const hasEmailNow = trimmedEmail.length > 0;
      if (emailChanged && !hasEmailNow) {
        // Explicit revoke
        emailPayload = null;
        passwordPayload = null;
      } else if (hasEmailNow && !hadWebAccess && !passwordChanged) {
        alert('Set a password to enable web access for this user.');
        return;
      } else {
        if (emailChanged) emailPayload = trimmedEmail;
        if (passwordChanged) passwordPayload = editExtPassword;
      }
    }

    setActionLoading(true);
    let detailsSaved = false;
    try {
      // Update name and/or mobile for external users if changed
      const updateData: {
        name?: string;
        mobile?: string;
        email?: string | null;
        password?: string | null;
      } = {};
      if (nameChanged) updateData.name = editExtName.trim();
      if (mobileChanged) updateData.mobile = editMobile.trim();
      if (emailPayload !== undefined) updateData.email = emailPayload;
      if (passwordPayload !== undefined) updateData.password = passwordPayload;
      if (Object.keys(updateData).length > 0) {
        await externalUsersAPI.update(
          editingAssignment.user_name,
          // The project is what a project Admin's right rests on.
          canEditExternalDetails ? updateData : { ...updateData, projectId: selectedProjectId ?? undefined },
        );
        detailsSaved = true;
      }

      // Update role if changed
      // The new role first: if it is refused, the person keeps the one they have.
      if (editRoleId !== editingAssignment.role_id) {
        await rolesAPI.assignRole(editingAssignment.user_name, editRoleId);
        await rolesAPI.removeRole(editingAssignment.user_name, editingAssignment.role_id);
      }

      fetchData();
      setShowEditModal(false);
      setEditingAssignment(null);
      setEditRoleId('');
      setEditMobile('');
      setEditMobileOriginal('');
      setEditExtEmail('');
      setEditExtEmailOriginal('');
      setEditExtPassword('');
      setEditExtHasWebAccessOriginal(false);
    } catch (err: any) {
      console.error('Error updating role:', err);
      fetchData();
      const reason = err.response?.data?.message || 'Failed to update role';
      alert(detailsSaved ? `This person's details were saved, but the role was not changed. ${reason}` : reason);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove
  const handleRemove = async (userId: string, roleId: string) => {
    setActionLoading(true);
    try {
      await rolesAPI.removeRole(userId, roleId);

      // An external user (EXT- prefix) is one person shared by every project they are
      // in. The server switches them off only when this was their last project.
      if (userId.startsWith('EXT-')) {
        try {
          await externalUsersAPI.delete(userId as any);
        } catch (extErr: any) {
          console.warn('Failed to deactivate external user:', extErr);
          // The role is already gone and the row is about to leave this list, so a
          // refusal to switch a super admin off has to be said here or not at all.
          if (extErr?.response?.data?.code === 'super_admin_only') {
            alert(`The role was removed, but the person was not switched off. ${extErr.response.data.message}`);
          } else if (extErr?.response?.status === 403) {
            alert('The role was removed, but this person can still sign in. Only a platform admin can switch them off.');
          }
        }
      }

      fetchData();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error removing role:', err);
      alert('Failed to remove role');
    } finally {
      setActionLoading(false);
    }
  };

  // Search all employees (super_admin)
  useEffect(() => {
    if (!showAssignModal || assignMode !== 'search' || externalSearch.trim().length < 2) {
      setExternalResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setExternalSearchLoading(true);
      try {
        const empCode = user?.userCode?.toString() || user?.userName || '';
        const res = await rolesAPI.searchAllEmployees(externalSearch.trim(), empCode);
        if (res.data.success) setExternalResults(res.data.data);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setExternalSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [externalSearch, showAssignModal, assignMode]);

  const handleExternalEmployeeToggle = (code: string) => {
    setSelectedExternalEmployees((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleAddAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedExternalEmployees.length === 0 || !externalRoleId) {
      alert('Please select at least one employee and a role');
      return;
    }
    setActionLoading(true);
    try {
      await Promise.all(
        selectedExternalEmployees.map((userId) =>
          rolesAPI.assignRole(userId, externalRoleId)
        )
      );
      fetchData();
      setShowAssignModal(false);
      setExternalSearch('');
      setExternalResults([]);
      setSelectedExternalEmployees([]);
      setExternalRoleId('');
    } catch (err: any) {
      console.error('Error assigning role:', err);
      alert(err.response?.data?.message || 'Failed to assign role');
    } finally {
      setActionLoading(false);
    }
  };

  // Everything the Add External User tab holds. Run whenever the tab is left —
  // closed, cancelled, switched away from, or submitted — so nothing typed for
  // one person (a password above all) is still there for the next.
  const resetExternalUserForm = () => {
    setExtUserName('');
    setExtUserMobile('');
    setExtUserRoleId('');
    setExtUserEmail('');
    setExtUserPassword('');
    setExtUserShowPassword(false);
    setExtExistingUser(null);
    setExtLookupConflict(null);
    setExtChangePassword(false);
    setPendingExtPropagationConfirm(null);
    nameBeforeMatch.current = null;
  };

  // Handle external user creation + role assignment
  const handleExternalUserAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!extUserName.trim() || !extUserMobile.trim() || !extUserRoleId) {
      alert('Please fill in name, mobile number, and select a role');
      return;
    }
    const cleanMobile = extUserMobile.trim().replace(/\s+/g, '');
    if (!/^[6-9]\d{9}$/.test(cleanMobile)) {
      alert('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    // Web-access validation: for NEW users, both email + password or neither.
    // For REUSED identities with an existing password, password input is
    // optional (admin must explicitly toggle "Change password" to override).
    const trimmedEmail = extUserEmail.trim();
    const hasEmail = trimmedEmail.length > 0;
    const hasPassword = extUserPassword.length > 0;
    const reusingWithStoredPassword = lockedToExisting || (!!extExistingUser?.hasPassword && !extChangePassword);
    if (!reusingWithStoredPassword && hasEmail !== hasPassword) {
      alert('Both email and password are required to grant web access. Leave both blank for mobile-only access.');
      return;
    }
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      alert('Please enter a valid email address');
      return;
    }
    if (hasPassword && extUserPassword.trim().length === 0) {
      alert('Password cannot be blank');
      return;
    }
    if (hasPassword && extUserPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    if (!selectedProjectId) {
      alert('No project selected');
      return;
    }
    if (extLookupConflict) {
      alert(extLookupConflict);
      return;
    }

    const toSend = externalUserCredentialsToSend({
      email: extUserEmail,
      password: extUserPassword,
      existingHasPassword: lockedToExisting || !!extExistingUser?.hasPassword,
      changePassword: extChangePassword,
    });

    // If we matched an existing identity AND the admin edited any field, we
    // need a confirmation step — the change will propagate to every project.
    const reusing = !!extExistingUser;
    if (reusing && extExistingUser && !lockedToExisting) {
      const trimmedName = extUserName.trim();
      const editedName = trimmedName !== extExistingUser.name;
      const editedMobile = cleanMobile !== extExistingUser.mobile;
      const editedEmail = hasEmail && trimmedEmail.toLowerCase() !== (extExistingUser.email || '').toLowerCase();
      const editedPassword = toSend.password !== undefined;
      if (editedName || editedMobile || editedEmail || editedPassword) {
        // Surface the confirmation modal — exits this submit invocation.
        // Modal CTA replays the submit by invoking the stored callback.
        setPendingExtPropagationConfirm(() => () => {
          setPendingExtPropagationConfirm(null);
          submitExternalUser({ toSend, cleanMobile });
        });
        return;
      }
    }

    submitExternalUser({ toSend, cleanMobile });
  };

  // Inner submitter — broken out so the propagation-confirmation modal can
  // replay the request after the admin confirms.
  const submitExternalUser = async (ctx: {
    toSend: ExternalUserCredentialsToSend;
    cleanMobile: string;
  }) => {
    if (!selectedProjectId) return;
    const { toSend, cleanMobile } = ctx;
    setActionLoading(true);
    let saved: { employee_code: string; name: string } | null = null;
    try {
      const empCode = user?.userCode?.toString() || user?.userName || '';
      // Step 1: Create or reuse external user
      const createRes = await externalUsersAPI.create({
        name: extUserName.trim(),
        mobile: cleanMobile,
        projectId: selectedProjectId,
        createdBy: empCode,
        // A matched person keeping their password sends no password (the
        // server preserves it); everyone else sends what was typed.
        email: toSend.email,
        password: toSend.password,
        // A project Admin's save is refused before anything is written if this role is not theirs to give.
        roleId: extUserRoleId,
      });
      const payload = createRes.data || {};
      const newUser = payload.data;
      if (!newUser?.employee_code) throw new Error('Failed to create external user');
      saved = newUser;

      // Step 2: Assign role for THIS project (idempotent — server upserts)
      await rolesAPI.assignRole(newUser.employee_code, extUserRoleId);

      fetchData();
      setShowAssignModal(false);
      resetExternalUserForm();
      setAssignMode('project');
      const reused = payload.reused === true;
      if (reused && !newUser.currentProjects) {
        // The server does not tell a project's Admin which other projects a person is in.
        alert(`${newUser.name} is now part of this project.`);
      } else if (reused) {
        const totalProjects = new Set([...newUser.currentProjects.map((p: { projectId: number }) => p.projectId), selectedProjectId]).size;
        alert(`${newUser.name} is now part of ${totalProjects} project${totalProjects === 1 ? '' : 's'}.`);
      } else {
        const accessNote = toSend.email
          ? ` They can log in via OTP on mobile or with ${toSend.email} on the web.`
          : ' They can now login via OTP on their mobile.';
        alert(`External user created: ${newUser.name} (${newUser.employee_code}).${accessNote}`);
      }
    } catch (err: any) {
      console.error('Error creating external user:', err);
      const reason = err.response?.data?.message || err.message || 'Failed to create external user';
      if (saved) {
        // The person exists now, and only the role failed (a role can refuse who may give
        // it). Trying another role must match them, not send their password a second time.
        setExtExistingUser({
          employeeCode: saved.employee_code,
          name: saved.name,
          mobile: cleanMobile,
          email: toSend.email ?? null,
          hasPassword: toSend.password !== undefined || !!extExistingUser?.hasPassword,
          isActive: true,
          currentProjects: extExistingUser?.currentProjects ?? [],
        });
        setExtUserPassword('');
        setExtChangePassword(false);
        alert(`${saved.name} (${saved.employee_code}) was saved, but the role was not given. ${reason}`);
      } else {
        alert(reason);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <CenteredLoader />;
  }

  if (error) {
    return <RetryErrorState error={error} onRetry={fetchData} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Role Assignment</h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign roles to users to manage their permissions
          </p>
        </div>
        <button
          onClick={() => setShowAssignModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
        >
          <UserPlus className="w-4 h-4" />
          {isAnyAdmin ? 'Add & Assign Role' : 'Assign Role'}
        </button>
      </div>

      {/* Search + Role Filter */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user name, ID, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="relative sm:w-56">
            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white appearance-none"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Active employee count — matches the count shown under each role on
          Access Management so the two pages can be cross-checked. */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Users className="w-4 h-4 text-primary-600" />
        <span>
          <span className="font-semibold text-gray-900">
            {roleScopedActiveAssignments.length.toLocaleString()}
          </span>{' '}
          active {roleScopedActiveAssignments.length === 1 ? 'employee' : 'employees'}
          {selectedRoleName ? (
            <>
              {' '}with role{' '}
              <span className="font-medium text-gray-900">{selectedRoleName}</span>
            </>
          ) : (
            <> across all roles</>
          )}
          {searchQuery && (
            <span className="text-gray-500">
              {' '}— showing {filteredAssignments.length.toLocaleString()} matching "{searchQuery}"
            </span>
          )}
        </span>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/70 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Employee Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Employee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Mobile Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Assigned On
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {assignment.user_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {getEmployeeName(assignment.user_name)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {getEmployeeMobile(assignment) || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-gray-900">
                        {assignment.role_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const isActive = getEmployeeActiveStatus(assignment.user_name);
                      if (isActive === undefined) return <span className="text-xs text-gray-400">—</span>;
                      return isActive ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">Inactive</span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(assignment.assigned_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <TableActionGroup>
                      <TableActionButton action="edit" label="Edit Role"
                        onClick={() => handleEditAssignment(assignment)}
                        title="Edit Role" />
                      <TableActionButton action="unassign" label="Remove"
                        onClick={() =>
                          setShowDeleteConfirm({
                            userId: assignment.user_name,
                            roleId: assignment.role_id,
                          })
                        }
                        title="Remove" />
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAssignments.length === 0 && (
          <div className="text-center py-12">
            <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No assignments found</h3>
            <p className="text-sm text-gray-500">
              {searchQuery ? 'Try a different search term' : 'Assign roles to users to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
            <div className="shrink-0 bg-primary-600 rounded-t-xl px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">{isAnyAdmin ? 'Add & Assign Role' : 'Assign Role to User'}</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignForm({ user_name: '', role_id: '' });
                  setEmployeeSearch('');
                  setSelectedBudget('');
                  setSelectedEmployees([]);
                  setSelectAll(false);
                  setAssignMode('project');
                  setExternalSearch('');
                  setExternalResults([]);
                  setSelectedExternalEmployees([]);
                  setExternalRoleId('');
                  resetExternalUserForm();
                }}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={assignMode === 'external' ? handleExternalUserAssign : assignMode === 'search' ? handleAddAssign : handleAssign} className="min-h-0 overflow-y-auto rounded-b-xl px-6 pt-6 space-y-4">
              {/* Admin & Super Admin: Toggle between project employees, search all, and external user */}
              {(isAnyAdmin || externalLevel !== null) && (
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setAssignMode('project'); setExternalSearch(''); setExternalResults([]); setSelectedExternalEmployees([]); setExternalRoleId(''); resetExternalUserForm(); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${assignMode === 'project' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Project Employees
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAssignMode('search'); setSelectedBudget(''); setSelectedEmployees([]); setSelectAll(false); setEmployeeSearch(''); resetExternalUserForm(); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${assignMode === 'search' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Search All Employees
                  </button>
                  {externalLevel !== null && (
                    <button
                      type="button"
                      onClick={() => { setAssignMode('external'); setSelectedBudget(''); setSelectedEmployees([]); setSelectAll(false); setEmployeeSearch(''); setExternalSearch(''); setExternalResults([]); setSelectedExternalEmployees([]); setExternalRoleId(''); }}
                      className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${assignMode === 'external' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Add External User
                    </button>
                  )}
                </div>
              )}

              {assignMode === 'external' ? (
                <>
                  {/* Existing-user banner */}
                  {extLookupConflict && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      {extLookupConflict}
                    </div>
                  )}
                  {extExistingUser && !extLookupConflict && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <div className="font-medium">
                        {extExistingUser.name} ({extExistingUser.employeeCode}) is already an external user.
                      </div>
                      {lockedToExisting ? (
                        <div className="mt-1 text-xs">
                          {extExistingUser.isActive === false
                            ? 'Their account is switched off. Only a platform admin can switch it back on.'
                            : 'Adding here will give them access to this project too. Their details stay as they are; only a platform admin can change them.'}
                        </div>
                      ) : (
                        <>
                          <div className="mt-1 text-xs">
                            Currently in: {extExistingUser.currentProjects.length === 0
                              ? '— (no project memberships)'
                              : extExistingUser.currentProjects.map((p) => p.projectName || `#${p.projectId}`).join(', ')
                            }. Adding here will give them access to this project too — their existing details will be reused.
                          </div>
                          <div className="mt-2 text-xs italic">
                            Editing any field below updates the user across all their projects.
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {/* External User Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter full name..."
                      value={extUserName}
                      onChange={(e) => setExtUserName(e.target.value)}
                      readOnly={lockedToExisting}
                      className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${lockedToExisting ? 'bg-gray-50 text-gray-700' : ''}`}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      placeholder="Enter 10-digit mobile number..."
                      value={extUserMobile}
                      onChange={(e) => setExtUserMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      maxLength={10}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">This number will be used for OTP login on the mobile app</p>
                  </div>
                  {!addsByNameAndMobile && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">ChannelPlex web access (optional)</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Leave blank if the user only needs the mobile app. Fill both fields to let them log in on the web.
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          placeholder="user@example.com"
                          value={extUserEmail}
                          onChange={(e) => setExtUserEmail(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
                        {extExistingUser?.hasPassword && !extChangePassword ? (
                          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">
                            <span>Using existing password</span>
                            <button
                              type="button"
                              onClick={() => { setExtChangePassword(true); setExtUserPassword(''); }}
                              className="text-xs font-medium text-primary-600 hover:text-primary-700"
                            >
                              Change password
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type={extUserShowPassword ? 'text' : 'password'}
                              placeholder={extChangePassword ? 'New password — applies to all projects' : 'At least 6 characters'}
                              value={extUserPassword}
                              onChange={(e) => setExtUserPassword(e.target.value)}
                              className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setExtUserShowPassword((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-primary-600 hover:text-primary-700"
                            >
                              {extUserShowPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Select Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={extUserRoleId}
                      onChange={(e) => setExtUserRoleId(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Choose a role...</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : assignMode === 'project' ? (
                <>
                  {/* Budget Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Select Budget <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedBudget}
                      onChange={(e) => {
                        setSelectedBudget(e.target.value);
                        setAssignForm({ ...assignForm, user_name: '' });
                        setEmployeeSearch('');
                        setSelectedEmployees([]);
                        setSelectAll(false);
                      }}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Choose a budget...</option>
                      {distinctBudgets.map((budget) => (
                        <option key={budget.id} value={budget.id.toString()}>
                          {budget.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* User Selection with Checkboxes */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Users <span className="text-red-500">*</span>
                      </label>
                      {selectedBudget && (
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {selectAll ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>

                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={selectedBudget ? "Search employees..." : "Please select a budget first"}
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        disabled={!selectedBudget}
                        className={`w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${!selectedBudget ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
                      />
                    </div>

                    {selectedBudget && (
                      <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => {
                            const code = emp.emp_code || emp.empCode || emp.employeeCode || emp.id;
                            const name = emp.emp_name || emp.empName || emp.employeeName || '';
                            const isSelected = selectedEmployees.includes(code);
                            return (
                              <label
                                key={code}
                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                  isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleEmployeeToggle(code)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-900">
                                  {name} <span className="text-gray-500">({code})</span>
                                </span>
                              </label>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            No employees found
                          </div>
                        )}
                      </div>
                    )}

                    {selectedEmployees.length > 0 && (
                      <p className="mt-1 text-xs text-green-600">
                        {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Search All Employees */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Search Employee <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, employee code, or email (min 2 chars)..."
                        value={externalSearch}
                        onChange={(e) => setExternalSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {(filteredExternalResults.length > 0 || externalSearchLoading) && (
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      {externalSearchLoading ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                          <InlineLoader label="Searching..." />
                        </div>
                      ) : (
                        filteredExternalResults.map((emp) => {
                          const isSelected = selectedExternalEmployees.includes(emp.employee_code);
                          return (
                            <label
                              key={emp.employee_code}
                              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleExternalEmployeeToggle(emp.employee_code)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-900">
                                {emp.employee_name} <span className="text-gray-500">({emp.employee_code})</span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}

                  {externalSearch.trim().length >= 2 && !externalSearchLoading && filteredExternalResults.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-2">
                      {externalResults.length > 0
                        ? 'All matching employees already have a role assigned in this project'
                        : 'No employees found'}
                    </div>
                  )}

                  {selectedExternalEmployees.length > 0 && (
                    <p className="text-xs text-green-600">
                      {selectedExternalEmployees.length} employee{selectedExternalEmployees.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </>
              )}

              {/* Role Selection (shared for project/search modes only — external has its own) */}
              {assignMode !== 'external' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Select Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={assignMode === 'search' ? externalRoleId : assignForm.role_id}
                    onChange={(e) => assignMode === 'search' ? setExternalRoleId(e.target.value) : setAssignForm({ ...assignForm, role_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Choose a role...</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignForm({ user_name: '', role_id: '' });
                    setEmployeeSearch('');
                    setSelectedBudget('');
                    setSelectedEmployees([]);
                    setSelectAll(false);
                    setAssignMode('project');
                    setExternalSearch('');
                    setExternalResults([]);
                    setSelectedExternalEmployees([]);
                    setExternalRoleId('');
                    resetExternalUserForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || (
                    assignMode === 'external'
                      ? (!extUserName.trim() || !extUserMobile.trim() || extUserMobile.trim().length !== 10 || !extUserRoleId)
                      : assignMode === 'project'
                        ? (selectedEmployees.length === 0 || !assignForm.role_id)
                        : (selectedExternalEmployees.length === 0 || !externalRoleId)
                  )}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {assignMode === 'external'
                    ? (extExistingUser ? 'Add to this Project' : 'Create & Assign Role')
                    : (() => {
                        const count = assignMode === 'search' ? selectedExternalEmployees.length : selectedEmployees.length;
                        return `Assign Role to ${count} User${count !== 1 ? 's' : ''}`;
                      })()
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Propagation Confirmation Modal — when admin edits a reused identity */}
      {pendingExtPropagationConfirm && extExistingUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Update across all projects?</h3>
            <p className="text-sm text-gray-600 mb-4">
              {extExistingUser.name} is in {extExistingUser.currentProjects.length} project{extExistingUser.currentProjects.length === 1 ? '' : 's'} today
              ({extExistingUser.currentProjects.map((p) => p.projectName || `#${p.projectId}`).join(', ')}).
              Saving these changes will update their details across all of them, plus the project you're adding them to now.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingExtPropagationConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => pendingExtPropagationConfirm()}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, update everywhere
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmOverlay
          title="Remove Role"
          onCancel={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleRemove(showDeleteConfirm.userId, showDeleteConfirm.roleId)}
          loading={actionLoading}
          confirmLabel="Remove"
        >
          Are you sure you want to remove this role from the user? They will lose all permissions associated with this role.
        </DeleteConfirmOverlay>
      )}

      {/* Edit Role Modal */}
      {showEditModal && editingAssignment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-xl bg-white shadow-xl">
            <div className="shrink-0 bg-primary-600 rounded-t-xl px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Edit User Role</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAssignment(null);
                  setEditRoleId('');
                  setEditMobile('');
                  setEditMobileOriginal('');
                }}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateRole} className="min-h-0 overflow-y-auto rounded-b-xl px-6 pt-6 space-y-4">
              {/* User Info — read-only for regular users, editable for external users */}
              {editingAssignment.user_name.startsWith('EXT-') && mayEditExternalDetails ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      User ID
                    </label>
                    <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                      {editingAssignment.user_name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter full name..."
                      value={editExtName}
                      onChange={(e) => setEditExtName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      placeholder="Enter 10-digit mobile number..."
                      value={editMobile}
                      onChange={(e) => setEditMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      maxLength={10}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">This number is used for OTP login on the mobile app</p>
                  </div>
                  {canEditExternalDetails && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">ChannelPlex web access</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {editExtHasWebAccessOriginal
                            ? 'Edit email to change the login ID. Leave password blank to keep the existing one; clear both to revoke web access.'
                            : 'Add an email + password to let this user log in on the web.'}
                        </p>
                      </div>
                      {editExtHasWebAccessOriginal && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Enabled
                        </span>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        value={editExtEmail}
                        onChange={(e) => setEditExtEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {editExtHasWebAccessOriginal ? 'New password (leave blank to keep existing)' : 'Password'}
                      </label>
                      <div className="relative">
                        <input
                          type={editExtShowPassword ? 'text' : 'password'}
                          placeholder={editExtHasWebAccessOriginal ? 'Leave blank to keep existing' : 'At least 6 characters'}
                          value={editExtPassword}
                          onChange={(e) => setEditExtPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setEditExtShowPassword((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-primary-600 hover:text-primary-700"
                        >
                          {editExtShowPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                    {editExtHasWebAccessOriginal && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditExtEmail('');
                          setEditExtPassword('');
                        }}
                        className="text-xs font-medium text-red-600 hover:text-red-700"
                      >
                        Clear email &amp; password to revoke web access
                      </button>
                    )}
                  </div>
                  )}
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    User
                  </label>
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <Users className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getEmployeeName(editingAssignment.user_name)}
                      </p>
                      <p className="text-xs text-gray-500">ID: {editingAssignment.user_name}</p>
                    </div>
                  </div>
                  {editingAssignment.user_name.startsWith('EXT-') && accessKnown && (externalLevel === null || editExtDetails === 'not_listed') && (
                    <p className="mt-1 text-xs text-gray-500">
                      Only a platform admin can change this person's name, mobile number or login.
                    </p>
                  )}
                  {editingAssignment.user_name.startsWith('EXT-') && externalLevel === 'project' && editExtDetails === 'locked' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Only a platform admin can change this person's name or mobile number: they are linked to another project, or have access you do not.
                    </p>
                  )}
                  {editingAssignment.user_name.startsWith('EXT-') && externalLevel === 'project' && editExtDetails === 'failed' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Could not load this person's details. Close this window and open it again.
                    </p>
                  )}
                </div>
              )}

              {/* Current Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Current Role
                </label>
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{editingAssignment.role_name}</span>
                </div>
              </div>

              {/* New Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={editRoleId}
                  onChange={(e) => setEditRoleId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Choose a role...</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAssignment(null);
                    setEditRoleId('');
                    setEditMobile('');
                    setEditMobileOriginal('');
                    setEditExtEmail('');
                    setEditExtEmailOriginal('');
                    setEditExtPassword('');
                    setEditExtHasWebAccessOriginal(false);
                    setEditExtShowPassword(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    actionLoading ||
                    !editRoleId ||
                    (editRoleId === editingAssignment.role_id &&
                      editMobile.trim() === editMobileOriginal &&
                      editExtName.trim() === editExtNameOriginal &&
                      editExtEmail.trim() === (editExtEmailOriginal || '') &&
                      editExtPassword.length === 0)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Update Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
